<template>
  <div
    v-if="!!logData"
    data-cy="deal-communication-log-wrapper"
    class="flex max-w-80 min-w-50 cursor-pointer flex-col space-x-2"
    @click="openCommunicationLogsModal"
  >
    <last-call
      v-if="isCallLogData(logData)"
      data-cy="deal-communication-log-call"
      :model="{ last_call_session: logData }"
    />
    <last-email-sent
      v-else-if="isEmailLogData(logData)"
      data-cy="deal-communication-log-email"
      :model="{
        latest_email_sent: emailData,
      }"
    />
    <recent-note
      v-else-if="isNoteLogData(logData)"
      data-cy="deal-communication-log-note"
      :model="{ latest_note: logData }"
    />
    <last-sms
      v-else-if="isSmsLogData(logData)"
      data-cy="deal-communication-log-sms"
      :model="logData"
    />
    <last-task
      v-else-if="isTaskLogData(logData)"
      data-cy="deal-communication-log-task"
      :model="logData"
    />
    <send-deal-communication-modal
      v-if="allActivityModalDealId === model.id"
      :deal-id="model.id"
      :title="$t('DEALS.TABLE_HEADERS.ALL_ACTIVITY')"
      :type="allActivityType"
      @close-dialog="setAllActivityModalDealId(null)"
    >
      <template #content>
        <div class="m-6 h-full">
          <communication-logs class="h-[calc(100%-48px)] pb-12" />
        </div>
      </template>
    </send-deal-communication-modal>
  </div>
  <div v-else data-cy="deal-communication-log-empty">-</div>
</template>

<script setup lang="ts">
import { computed, nextTick } from "vue";

import { storeToRefs } from "pinia";

import { CommunicationType } from "@/enums/communicationLogs";
import { allActivityType } from "@/helpers/communication";
import router from "@/router";
import { useCommunicationStore } from "@/stores/communication";

import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import CommunicationLogs from "@/components/communicationLogs/communication/CommunicationLogs.vue";
import LastCall from "@/components/deals/LastCall.vue";
import LastEmailSent from "@/components/deals/LastEmailSent.vue";
import LastSms from "@/components/deals/LastSms.vue";
import LastTask from "@/components/deals/LastTask.vue";
import RecentNote from "@/components/deals/RecentNote.vue";

import type { IApplication, LatestSentEmail } from "@/models/applications";
import type {
  CallSession,
  DealCommunicationLog,
  EmailThread,
  SmsLogData,
  TaskLogData,
} from "@/models/communicationLogs";
import type { Note } from "@/models/notes";

const { value, model } = defineProps<{
  value: DealCommunicationLog | null;
  model: IApplication;
}>();

const communicationStore = useCommunicationStore();
const { setAllActivityModalDealId } = communicationStore;
const { allActivityModalDealId } = storeToRefs(communicationStore);

const logData = computed(() => value?.data || null);

const emailData = computed<LatestSentEmail | null>(() => {
  if (!isEmailLogData(logData.value)) {
    return null;
  }

  return {
    id: logData.value.id,
    sender_name: logData.value.sender?.name || "",
    template_name: logData.value?.template_name || "",
    subject: logData.value.subject || "",
    timestamp: logData.value.created_at,
  };
});

const isEmailLogData = (
  data: DealCommunicationLog["data"] | null
): data is EmailThread => value?.type === CommunicationType.email;

const isCallLogData = (
  data: DealCommunicationLog["data"] | null
): data is CallSession => value?.type === CommunicationType.call;

const isTaskLogData = (
  data: DealCommunicationLog["data"] | null
): data is TaskLogData => value?.type === CommunicationType.task;

const isSmsLogData = (
  data: DealCommunicationLog["data"] | null
): data is SmsLogData => value?.type === CommunicationType.sms;

const isNoteLogData = (
  data: DealCommunicationLog["data"] | null
): data is Note => value?.type === CommunicationType.note;

const openCommunicationLogsModal = async () => {
  setAllActivityModalDealId(null);
  await router.push({ query: { application_ids: model.id } });
  await nextTick();
  setAllActivityModalDealId(model.id);
};
</script>
